import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { saveUserAddress } from '../utils/supabaseEdgeApi';
import { useAuth } from '../contexts/AuthContext';
import LoadingOverlay from './LoadingOverlay';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const AddressModal = ({
  open,
  onClose,
  initialAddress = '',
  initialLat = '',
  initialLng = '',
  initialLocationLink = '',
  initialPincode = '',
  onSave,
  title = 'Add address',
  userId,
  token,
  persistAddress = true,
}) => {
  const [addressLine1, setAddressLine1] = useState(initialAddress);
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('Bangalore');
  const [stateField, setStateField] = useState('Karnataka');
  const [pincode, setPincode] = useState(initialPincode);
  const [locationLink, setLocationLink] = useState(initialLocationLink);
  const [lat, setLat] = useState(initialLat);
  const [lng, setLng] = useState(initialLng);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 });
  const [markerPosition, setMarkerPosition] = useState(null);
  const [locationError, setLocationError] = useState('');
  const { redirectToLogin } = useAuth();
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [locationLoading, setLoadingLocation] = useState(false);

  const buildLocationLink = (latValue, lngValue) => {
    if (!latValue || !lngValue) return '';
    return `https://maps.google.com/?q=${latValue},${lngValue}`;
  };

  const parseLocationLink = (link) => {
    if (!link) return null;
    try {
      const url = new URL(link.trim());
      const qValue = url.searchParams.get('q');
      if (qValue) {
        const [latValue, lngValue] = qValue?.split(',').map((v) => parseFloat(v.trim()));
        if (!Number.isNaN(latValue) && !Number.isNaN(lngValue)) {
          return { lat: latValue, lng: lngValue };
        }
      }
      const pathMatch = url.pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (pathMatch) {
        return { lat: parseFloat(pathMatch[1]), lng: parseFloat(pathMatch[2]) };
      }
      const queryMatch = link.match(/\?q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
      if (queryMatch) {
        return { lat: parseFloat(queryMatch[1]), lng: parseFloat(queryMatch[2]) };
      }
    } catch (error) {
      return null;
    }
    return null;
  };

  useEffect(() => {
    setAddressLine1(initialAddress);
    setAddressLine2('');
    setCity('Bangalore');
    setStateField('Karnataka');
    setPincode(initialPincode);
    setLat(initialLat);
    setLng(initialLng);
    setShowMap(open);
    setLocationLink(
      initialLocationLink || (initialLat && initialLng ? buildLocationLink(Number(initialLat), Number(initialLng)) : '')
    );

    if (initialLat && initialLng) {
      setMapCenter({ lat: Number(initialLat), lng: Number(initialLng) });
      setMarkerPosition({ lat: Number(initialLat), lng: Number(initialLng) });
    }
  }, [initialAddress, initialLat, initialLng, initialLocationLink, initialPincode, open]);

  useEffect(() => {
    const parsed = parseLocationLink(locationLink);
    if (parsed && parsed.lat && parsed.lng) {
      if (parsed.lat !== lat || parsed.lng !== lng) {
        setLat(parsed.lat);
        setLng(parsed.lng);
        setMapCenter({ lat: parsed.lat, lng: parsed.lng });
        setMarkerPosition({ lat: parsed.lat, lng: parsed.lng });
        setShowMap(true);
        reverseGeocode(parsed.lat, parsed.lng);
      }
    }
  }, [locationLink, lat, lng]);

  const reverseGeocode = async (latValue, lngValue) => {
    try {
      setLoadingLocation(true);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latValue}&lon=${lngValue}`
      );
      const data = await response.json();
      if (data?.display_name) {
        setAddressLine1(data.display_name);
      }

      const detectedPincode = data?.address?.postcode;
      if (detectedPincode) {
        setPincode(detectedPincode);
      }
      const detectedCity = data?.address?.city || data?.address?.town || data?.address?.village;
      if (detectedCity) {
        setCity(detectedCity);
      }
      const detectedState = data?.address?.state;
      if (detectedState) {
        setStateField(detectedState);
      }
    } catch (error) {
      console.error('Reverse geocode failed', error);
    } finally {
      setLoadingLocation(false);
    }
  };

  const fetchLocationSuggestions = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    const normalizedQuery = query.trim();
    const queryWithCity = /bangalore|bengaluru/i.test(normalizedQuery)
      ? normalizedQuery
      : `${normalizedQuery} Bangalore`;

    const parseNominatim = (data) =>
      (data || []).map((place) => ({
        id: place.place_id,
        label: place.display_name,
        address: place.display_name,
        lat: parseFloat(place.lat),
        lng: parseFloat(place.lon),
      }));

    const parsePhoton = (data) =>
      (data.features || []).map((feature) => ({
        id: `${feature.properties.osm_type}-${feature.properties.osm_id}`,
        label:
          feature.properties.name ||
          [feature.properties.street, feature.properties.city, feature.properties.state, feature.properties.country]
            .filter(Boolean)
            .join(', '),
        address:
          feature.properties.name ||
          [feature.properties.street, feature.properties.city, feature.properties.state, feature.properties.country]
            .filter(Boolean)
            .join(', '),
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0],
      }));

    try {
      setLoadingLocation(true);
      let items = [];

      const nominatimResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(
          queryWithCity
        )}&addressdetails=1&limit=8&countrycodes=in&viewbox=77.314,13.150,77.925,12.712&bounded=1`
      );
      const nominatimData = await nominatimResponse.json();
      items = parseNominatim(nominatimData);

      if (!items.length) {
        const photonResponse = await fetch(
          `https://photon.komoot.io/api/?q=${encodeURIComponent(normalizedQuery)}&limit=8&lang=en`
        );
        const photonData = await photonResponse.json();
        items = parsePhoton(photonData);
      }

      if (!items.length) {
        const broaderResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(
            normalizedQuery
          )}&addressdetails=1&limit=8&countrycodes=in`
        );
        const broaderData = await broaderResponse.json();
        items = parseNominatim(broaderData);
      }

      setSuggestions(items);
    } catch (error) {
      console.error('Location suggestion failed', error);
      setSuggestions([]);
    } finally {
      setLoadingLocation(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLocationSuggestions(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectSuggestion = async (item) => {
    setAddressLine1(item.address);
    const matchedPincode = item.address.match(/\d{6}/);
    if (matchedPincode) {
      setPincode(matchedPincode[0]);
    }
    setLat(item.lat);
    setLng(item.lng);
    setLocationLink(buildLocationLink(item.lat, item.lng));
    setSearchQuery(item.label);
    setMapCenter({ lat: item.lat, lng: item.lng });
    setMarkerPosition({ lat: item.lat, lng: item.lng });
    setSuggestions([]);
    setLocationError('');
    setShowMap(true);
    await reverseGeocode(item.lat, item.lng);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      return;
    }
    setLocationError('');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setMapCenter({ lat: latitude, lng: longitude });
        setMarkerPosition({ lat: latitude, lng: longitude });
        setLat(latitude);
        setLng(longitude);
        setLocationLink(buildLocationLink(latitude, longitude));
        setShowMap(true);
        await reverseGeocode(latitude, longitude);
      },
      (error) => {
        setLocationError('Unable to fetch location. Please allow location access or enter manually.');
        console.error(error);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const fetchSelectedMapAddress = async () => {
    const coords = markerPosition || mapCenter;
    if (!coords) {
      setLocationError('Select a point on the map or use current location first.');
      return;
    }
    setLat(coords.lat);
    setLng(coords.lng);
    setLocationLink(buildLocationLink(coords.lat, coords.lng));
    await reverseGeocode(coords.lat, coords.lng);
    setShowMap(true);
  };

  function MapUpdater({ center }) {
    const map = useMap();
    useEffect(() => {
      map.setView(center);
    }, [center, map]);
    return null;
  }

  function LocationMarker() {
    useMapEvents({
      click(e) {
        const { lat: clickLat, lng: clickLng } = e.latlng;
        setMarkerPosition({ lat: clickLat, lng: clickLng });
        setMapCenter({ lat: clickLat, lng: clickLng });
        setLat(clickLat);
        setLng(clickLng);
        setLocationLink(buildLocationLink(clickLat, clickLng));
        reverseGeocode(clickLat, clickLng);
      },
    });

    return markerPosition ? (
      <Marker position={markerPosition}>
        <Popup>{addressLine1 || 'Selected location'}</Popup>
      </Marker>
    ) : null;
  }

  const handleSave = async () => {
    if (!addressLine1?.trim()) {
      setSaveError('Address line 1 cannot be empty.');
      return;
    }
    if (!pincode || !/^56\d{4}$/.test(pincode)) {
      setSaveError('Enter a valid Bangalore pincode starting with 56.');
      return;
    }
    setSaveError('');
    setSaving(true);
    try {
      const formattedAddress = [
        addressLine1.trim(),
        addressLine2.trim(),
        city.trim(),
        stateField.trim(),
        pincode.trim(),
      ]
        .filter(Boolean)
        .join(', ');

      if (token && userId && persistAddress) {
        await saveUserAddress({
          userId,
          token,
          address: formattedAddress,
          pincode,
          locationLink,
          lat,
          lng,
        });
      }
      onSave({ address: formattedAddress, pincode, locationLink, lat, lng });
      onClose();
    } catch (error) {
      console.error(error);
      if (error?.status === 401) {
        redirectToLogin();
        return;
      }
      setSaveError(error.message || 'Unable to save address.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box
        sx={{
          position: 'relative',
          p: 3,
          bgcolor: '#fff',
          width: '100%',
          maxWidth: 620,
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: 2,
          boxShadow: 24,
        }}
      >
        <LoadingOverlay open={locationLoading || saving} message={saving ? 'Saving address...' : 'Loading location...'} />
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
       
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Address details
          </Typography>
          <TextField
            label="Address line 1"
            value={addressLine1}
            fullWidth
            required
            onChange={(e) => setAddressLine1(e.target.value)}
            margin="normal"
            helperText="Apartment, building, house number, or street"
          />
          <TextField
            label="Address line 2 (Landmark / Area)"
            value={addressLine2}
            fullWidth
            onChange={(e) => setAddressLine2(e.target.value)}
            margin="normal"
            helperText="Optional additional locality information"
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              fullWidth
              margin="normal"
              disabled
            />
            <TextField
              label="State"
              value={stateField}
              onChange={(e) => setStateField(e.target.value)}
              fullWidth
              margin="normal"
              disabled
            />
          </Box>
          <TextField
            label="Pincode"
            value={pincode}
            fullWidth
            required
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              if (value && !/^56/.test(value)) {
                setLocationError('Bangalore pincodes must start with 56');
              } else {
                setLocationError('');
              }
              setPincode(value);
            }}
            margin="normal"
            inputProps={{ maxLength: 6 }}
            helperText={locationError || 'Bangalore pincodes start with 56 (560001-560105)'}
            error={!!locationError}
          />
          <TextField
            label="Google Maps location link"
            value={locationLink}
            fullWidth
            onChange={(e) => setLocationLink(e.target.value)}
            margin="normal"
            helperText="Paste a Google Maps URL such as https://maps.google.com/?q=lat,lng"
          />
          <TextField
            label="Search address"
            value={searchQuery}
            fullWidth
            onChange={(e) => setSearchQuery(e.target.value)}
            margin="dense"
            helperText="Search street, locality, or landmark"
          />
          {suggestions.length > 0 && (
            <Box sx={{ maxHeight: 180, overflowY: 'auto', mt: 1, borderRadius: 1, border: '1px solid #ddd', p: 1, bgcolor: '#fff' }}>
              {suggestions.map((item) => (
                <Button
                  key={item.id}
                  fullWidth
                  variant="outlined"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 1 }}
                  onClick={() => handleSelectSuggestion(item)}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
          {(lat || lng) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <TextField
                label="Latitude"
                value={lat}
                fullWidth
                InputProps={{ readOnly: true }}
                margin="normal"
              />
              <TextField
                label="Longitude"
                value={lng}
                fullWidth
                InputProps={{ readOnly: true }}
                margin="normal"
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Button size="small" variant="outlined" onClick={useCurrentLocation}>
              Use Current Location
            </Button>
            <Button size="small" variant="outlined" onClick={fetchSelectedMapAddress} disabled={!markerPosition && !showMap}>
              Fetch Address from Map
            </Button>
          </Box>
          {showMap ? (
            <Box sx={{ position: 'relative', height: { xs: 320, sm: 360 }, width: '100%', mb: 2, mt: 1 }}>
              <MapContainer
                center={[mapCenter.lat, mapCenter.lng]}
                zoom={12}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%', borderRadius: 8 }}
              >
                <MapUpdater center={mapCenter} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <LocationMarker />
              </MapContainer>
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  right: 12,
                  zIndex: 10,
                  bgcolor: 'rgba(255,255,255,0.98)',
                  borderRadius: 2,
                  p: 1,
                  boxShadow: 2,
                  maxHeight: '45%',
                  overflowY: 'auto',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Button size="small" variant="outlined" onClick={useCurrentLocation}>
                    Use Current Location
                  </Button>
                  <Button size="small" variant="outlined" onClick={fetchSelectedMapAddress} disabled={!markerPosition && !showMap}>
                    Fetch Address from Map
                  </Button>
                </Box>
                {locationError ? (
                  <Typography color="error" sx={{ mt: 1, mb: 1 }}>
                    {locationError}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', height: { xs: 240, sm: 280 }, width: '100%', mb: 2, mt: 1, borderRadius: 2, bgcolor: '#f4f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: '#666', textAlign: 'center', px: 2 }}>
                Click "Use Current Location" or choose an address to show the map and suggestions inside the picker.
              </Typography>
            </Box>
          )}
        </Box>
        {saveError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save address'}
          </Button>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddressModal;
